import type { Unit } from '@dynatrace-sdk/units';

import type { Formatter } from '../../core/types/formatter.js';
import { DEFAULT_ROOT_NODE_NAME } from '../constants.js';
import { getAppliedFormatter } from './get-applied-formatter.js';
import type {
  InternalTreeMapNode,
  TreeMapFormatter,
} from '../types/internal.js';
import {
  TreeMapRootNode,
  type TreeMapClusterNode,
  type TreeMapLeafNode,
} from '../types/treemap.js';

const getNodeId = (clusterName: string, leafName?: string) => {
  const name = leafName
    ? `${clusterName}-${leafName}`
    : `cluster-${clusterName}`;
  return name;
};

const getMappedLeafNode = (
  leafNode: TreeMapLeafNode,
  clusterName: string,
  formatter: Formatter,
  unit?: string | Unit,
) => {
  return {
    ...leafNode,
    id: getNodeId(clusterName, leafNode.name),
    formattedValue: formatter(leafNode.value ?? 0, unit),
  };
};

const getMappedCluster = (
  cluster: TreeMapClusterNode,
  formatter: Formatter,
  unit?: string | Unit,
) => {
  const clusterName = cluster.name;
  const clusterId = getNodeId(clusterName);
  // 'nodes' are mapped to 'children' => nodes are removed from the cluster
  const { nodes, ...clusterRest } = cluster;
  return {
    ...clusterRest,
    id: clusterId,
    children: (cluster.nodes ?? []).map((leaf) =>
      getMappedLeafNode(leaf, clusterName, formatter, unit),
    ),
  };
};

/**
 * Map nodes to children prop
 * @param rootNode - TreeMap root node
 * @returns - InternalTreeMapNode
 */
export const getMappedTree = (
  rootNode: TreeMapRootNode,
  formatter: TreeMapFormatter,
  unit?: string | Unit,
): InternalTreeMapNode => {
  const clusters = [...(rootNode.nodes ?? [])];

  const appliedFormatter = getAppliedFormatter(formatter);
  const children = clusters.map((cluster) =>
    getMappedCluster(cluster, appliedFormatter, unit),
  );

  const rootNodeName = rootNode.name ?? DEFAULT_ROOT_NODE_NAME;

  // 'nodes' are mapped to 'children' => 'nodes' are removed from the rootNode
  const { nodes, ...rootNodeRest } = rootNode;

  return {
    ...rootNodeRest,
    children,
    id: DEFAULT_ROOT_NODE_NAME,
    name: rootNodeName,
  };
};
